import {
  sendPasswordResetEmail,
  sendEmailVerification,
  updatePassword,
  verifyBeforeUpdateEmail,
  reload,
  User,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth'
import { auth } from '@/utils/firebaseConfig'
import { logger } from '@/utils/logger'

/**
 * パスワードリセットメールを送信
 */
export async function sendPasswordReset(email: string): Promise<void> {
  try {
    await sendPasswordResetEmail(auth, email)
  } catch (error) {
    logger.error('Error sending password reset email', error as Error)
    throw error
  }
}

/**
 * メール認証を送信
 */
export async function sendVerificationEmail(user?: User): Promise<void> {
  try {
    const currentUser = user || auth.currentUser
    if (!currentUser) {
      throw new Error('No authenticated user found')
    }
    const actionCodeSettings = {
      url: window.location.origin,
    }
    await sendEmailVerification(currentUser, actionCodeSettings)
  } catch (error) {
    logger.error('Error sending verification email', error as Error)
    throw error
  }
}

/**
 * パスワードを変更
 */
export async function changePassword(newPassword: string): Promise<void> {
  try {
    const currentUser = auth.currentUser
    if (!currentUser) {
      throw new Error('No authenticated user found')
    }
    await updatePassword(currentUser, newPassword)
  } catch (error) {
    logger.error('Error updating password', error as Error)
    throw error
  }
}

/**
 * ユーザー情報を再読み込みして最新の認証状態を取得
 */
export async function reloadUser(): Promise<User | null> {
  try {
    const currentUser = auth.currentUser
    if (!currentUser) {
      return null
    }
    await reload(currentUser)
    return currentUser
  } catch (error) {
    logger.error('Error reloading user', error as Error)
    throw error
  }
}

/**
 * メール認証状態を確認
 */
export function isEmailVerified(): boolean {
  const currentUser = auth.currentUser
  return currentUser?.emailVerified ?? false
}

/**
 * 現在のユーザーを取得
 */
export function getCurrentUser(): User | null {
  return auth.currentUser
}

/**
 * ユーザーの再認証（パスワード確認）
 */
export async function reauthenticateUser(password: string): Promise<void> {
  try {
    const currentUser = auth.currentUser
    if (!currentUser || !currentUser.email) {
      throw new Error('No authenticated user found')
    }

    const credential = EmailAuthProvider.credential(currentUser.email, password)
    await reauthenticateWithCredential(currentUser, credential)
  } catch (error) {
    console.error('Error reauthenticating user:', error)
    throw error
  }
}

/**
 * メールアドレスを変更
 * 新しいメールアドレスに認証メールを送信し、ユーザーが認証後に自動的にメールが更新される
 */
export async function changeEmail(newEmail: string): Promise<void> {
  try {
    const currentUser = auth.currentUser
    if (!currentUser) {
      throw new Error('No authenticated user found')
    }

    // 新しいメールアドレスに認証メールを送信
    // ユーザーが認証リンクをクリックすると自動的にメールアドレスが更新される
    await verifyBeforeUpdateEmail(currentUser, newEmail)
  } catch (error) {
    logger.error('Error sending email verification', error as Error)
    throw error
  }
}

/**
 * メール認証状態をチェック
 * ユーザー情報を再読み込みして最新の認証状態を取得し、指定されたメールアドレスが認証されているかチェック
 */
export async function checkEmailVerificationStatus(targetEmail: string): Promise<{
  isVerified: boolean
  currentEmail: string | null
}> {
  try {
    const currentUser = auth.currentUser
    if (!currentUser) {
      throw new Error('No authenticated user found')
    }

    // ユーザー情報を再読み込みして最新の認証状態を取得
    await reload(currentUser)

    // 現在のユーザーのメールアドレスと認証状態を確認
    const isVerified = currentUser.emailVerified
    const currentEmail = currentUser.email

    // 指定されたメールアドレスが現在のユーザーのメールアドレスと一致し、認証されているかチェック
    if (currentEmail === targetEmail && isVerified) {
      return {
        isVerified: true,
        currentEmail: targetEmail, // Return the target email when verification is successful
      }
    }

    return {
      isVerified: false,
      currentEmail: currentEmail, // Return current email when not verified yet
    }
  } catch (error) {
    console.error('Error checking email verification status:', error)
    throw error
  }
}
